import { useState } from "react";
import { Product } from "../../common/models/product";
import { useNotifications } from "../../common/stateDefs";
import { classNames, sendAuthenticatedRequest } from "../../common/functions";

type NewProductsNotificationFormProps = {
  searchResults?: Product[];
  query?: string;
  compact?: boolean;
};

export const NewProductsNotificationForm = ({
  searchResults,
  query,
  compact = false,
}: NewProductsNotificationFormProps) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const addNotification = useNotifications((state) => state.addNotification);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      addNotification({
        id: String(Math.random()),
        title: "Invalid email",
        content: "Please enter a valid email address.",
        type: "error",
      });
      return;
    }

    const hist = JSON.parse(
      localStorage.getItem("totalUserSearchHistory") || "[]",
    );
    let minMismatchGrade: number | undefined = undefined;

    if (hist && hist.length > 0 && hist[hist.length - 1].query === query) {
      minMismatchGrade = hist[hist.length - 1].minMismatchGrade;
    }

    setIsSubmitting(true);

    // TODO: Implement the actual API call to save the notification preference
    sendAuthenticatedRequest(
      `${process.env.REACT_APP_BF_API_BASE_URL}/search-results-subscribe`,
      {
        method: "POST",
        jsonBody: {
          email,
          searchQuery: query,
          urlQueryString: window.location.search,
          searchResults,
          minMismatchGrade,
        },
      },
    )
      .then((response) => {
        addNotification({
          id: String(Math.random()),
          title: "Thank you",
          content: "We will reach out when new products arrive.",
          type: "success",
        });
        setEmail("");
      })
      .catch((error) => {
        console.error("Error subscribing:", error);
        addNotification({
          id: String(Math.random()),
          title: "Error",
          content: "Could not add your email. Please try again.",
          type: "error",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const renderCompact = () => {
    return (
      <div className="py-4 bg-gray-100 flex justify-center">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <div className="text-sm text-gray-900 self-center">
            Notify me of new inventory matching this search
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="block rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-1 border focus:outline-none focus:ring-1 focus:ring-[#003636] focus:border-[#00062e]"
            disabled={isSubmitting}
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center rounded-md bg-[#00062e] text-[#eeeeee] font-bold py-1 px-3 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Submitting..." : "Notify Me"}
          </button>
        </form>
      </div>
    );
  };

  if (compact) {
    return renderCompact();
  }

  return (
    <div
      className={classNames(
        "mt-12",
        (searchResults?.length || 0) > 0 ? "pt-8 border-t border-gray-200" : "",
      )}
    >
      <div className="max-w-2xl mx-auto text-center">
        <h3 className="text-lg font-medium text-gray-900">
          Be the first to know
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          Enter your email to receive notifications about new inventory matching
          your search.
        </p>
        <form
          onSubmit={handleSubmit}
          className="mt-6 flex gap-3 justify-center"
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="block w-full max-w-md rounded-md border-gray-300 shadow-sm sm:text-sm px-4 py-2 border focus:outline-none focus:ring-1 focus:ring-[#003636] focus:border-[#00062e]"
            disabled={isSubmitting}
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center rounded-md bg-[#00062e] text-[#eeeeee] font-bold py-1 px-3 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Submitting..." : "Notify Me"}
          </button>
        </form>
      </div>
    </div>
  );
};
